import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { finalize, timeout } from "rxjs";
import { ConfirmationService, MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { TagModule } from "primeng/tag";
import { TextareaModule } from "primeng/textarea";
import { ToastModule } from "primeng/toast";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { ArticleOrder } from "src/app/models/article-order";
import { Users } from "src/app/models/users";
import { ArticleOrdersService } from "src/app/services/article-orders.service";
import { UsersService } from "src/app/services/users.service";

@Component({
  standalone: true,
  selector: "app-ads-manager",
  templateUrl: "./ads-manager.component.html",
  styleUrl: "./ads-manager.component.less",
  imports: [CommonModule, FormsModule, RouterModule, ButtonModule, InputTextModule, SelectModule, TagModule, TextareaModule, ToastModule, ConfirmDialogModule, DialogModule],
})
export class AdsManagerComponent implements OnInit {
  orders: ArticleOrder[] = [];
  selected: ArticleOrder | null = null;
  events: any[] = [];
  loading = false;
  saving = false;
  search = "";
  status = "all";
  quoteAmount: number | null = null;
  billingEmail = "";
  paypalInvoiceId = "";
  paypalInvoiceUrl = "";
  quoteNotes = "";
  editorialNotes = "";
  publishedUrl = "";
  customer: Users | null = null;
  customerDialogVisible = false;
  customerLoading = false;

  readonly statuses = [
    { label: "All requests", value: "all" },
    { label: "Needs quote", value: "quote_requested" },
    { label: "Quote sent", value: "quote_sent" },
    { label: "Customer accepted", value: "accepted" },
    { label: "Payment pending", value: "payment_pending" },
    { label: "Paid", value: "paid" },
    { label: "Published", value: "published" },
  ];

  constructor(
    private ordersService: ArticleOrdersService,
    private usersService: UsersService,
    private messages: MessageService,
    private confirmations: ConfirmationService,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    setTimeout(() => this.load());
  }

  load(keepSelection = false): void {
    this.loading = true;
    this.ordersService.list({
      _limit: 200,
      _sort_field: "created_at",
      _sort_order: "DESC",
    }).pipe(
      timeout(15000),
      finalize(() => {
        this.loading = false;
        this.changeDetector.markForCheck();
      }),
    ).subscribe({
      next: (orders) => {
        this.orders = orders;
        if (keepSelection && this.selected) this.open(orders.find((item) => item.id === this.selected?.id) || null);
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        const detail = error?.error?.message
          || error?.error?.error
          || (error?.name === "TimeoutError" ? "The admin API did not respond within 15 seconds."
            : error?.status === 401 ? "Your session is not authorized. Please sign in again."
              : error?.status === 404 ? "The article orders API endpoint was not found."
                : `The admin API request failed${error?.status ? ` (HTTP ${error.status})` : ""}.`);
        this.messages.add({ severity: "error", summary: "Could not load requests", detail });
      },
    });
  }

  get visibleOrders(): ArticleOrder[] {
    const query = this.search.trim().toLowerCase();
    return this.orders.filter((order) => {
      const matchesStatus = this.status === "all" || order.status === this.status;
      const haystack = [order.campaign_name, order.company_name, order.customer_name, order.customer_email, order.target_url].join(" ").toLowerCase();
      return matchesStatus && (!query || haystack.includes(query));
    });
  }

  get needsAttention(): number { return this.orders.filter((o) => ["quote_requested", "under_review", "changes_requested"].includes(o.status)).length; }
  get pendingPayments(): number { return this.orders.filter((o) => o.payment_status === "pending").length; }
  get paidRevenue(): number { return this.orders.filter((o) => o.payment_status === "paid").reduce((sum, o) => sum + Number(o.approved_budget || 0), 0); }
  get publishedCount(): number { return this.orders.filter((o) => o.status === "published").length; }

  open(order: ArticleOrder | null): void {
    this.selected = order;
    this.events = [];
    if (!order) return;
    this.quoteAmount = Number(order.approved_budget || order.requested_budget || 0);
    this.billingEmail = order.billing_email || order.customer_email || "";
    this.paypalInvoiceId = order.payment_reference || "";
    this.paypalInvoiceUrl = order.paypal_invoice_url || "";
    this.quoteNotes = order.quote_notes || "";
    this.editorialNotes = order.editorial_notes || "";
    this.publishedUrl = order.published_url || "";
    this.ordersService.events(order.id).subscribe({ next: (events) => this.events = events });
  }

  openCustomer(): void {
    if (!this.selected) return;
    this.customer = null;
    this.customerDialogVisible = true;
    this.customerLoading = true;
    this.usersService.getId(this.selected.user_id).pipe(
      finalize(() => {
        this.customerLoading = false;
        this.changeDetector.markForCheck();
      }),
    ).subscribe({
      next: (customer) => {
        this.customer = customer;
        this.changeDetector.markForCheck();
      },
      error: () => {
        this.messages.add({ severity: "error", summary: "Could not load customer", detail: "The customer profile could not be retrieved." });
      },
    });
  }

  sendQuote(): void {
    if (!this.selected || !this.quoteAmount || this.quoteAmount <= 0 || !this.validEmail(this.billingEmail)) {
      this.messages.add({ severity: "warn", summary: "Missing quote details", detail: "Enter an approved amount and a valid billing email." });
      return;
    }
    this.saving = true;
    this.ordersService.quote(this.selected.id, {
      approved_budget: this.quoteAmount,
      quote_notes: this.quoteNotes,
      billing_email: this.billingEmail,
    }).subscribe({
      next: (result) => {
        this.saving = false;
        this.messages.add({
          severity: result.email_sent === false ? "warn" : "success",
          summary: "Quote approved",
          detail: result.email_sent === false ? "The quote was saved, but the email could not be sent." : "The customer received the quote by email.",
        });
        this.load(true);
      },
      error: (error) => this.fail(error),
    });
  }

  savePayPalInvoice(): void {
    if (!this.selected || !this.validEmail(this.billingEmail) || !this.paypalInvoiceId.trim() || (this.paypalInvoiceUrl && !this.validUrl(this.paypalInvoiceUrl))) {
      this.messages.add({ severity: "warn", summary: "Missing invoice details", detail: "Enter the billing email, PayPal invoice ID and, if supplied, a valid invoice URL." });
      return;
    }
    this.saving = true;
    this.ordersService.invoice(this.selected.id, {
      billing_email: this.billingEmail,
      payment_reference: this.paypalInvoiceId.trim(),
      paypal_invoice_url: this.paypalInvoiceUrl.trim() || undefined,
    }).subscribe({
      next: () => {
        this.saving = false;
        this.messages.add({ severity: "success", summary: "PayPal invoice saved", detail: "The request is now awaiting payment." });
        this.load(true);
      },
      error: (error) => this.fail(error),
    });
  }

  transition(action: string): void {
    if (!this.selected) return;
    if (action === "publish" && !this.validUrl(this.publishedUrl)) {
      this.messages.add({ severity: "warn", summary: "Published URL required", detail: "Enter the live article URL first." });
      return;
    }
    this.saving = true;
    this.ordersService.transition(this.selected.id, {
      action,
      editorial_notes: this.editorialNotes,
      published_url: action === "publish" ? this.publishedUrl : undefined,
    }).subscribe({
      next: () => {
        this.saving = false;
        this.messages.add({ severity: "success", summary: "Order updated", detail: "The workflow status has been saved." });
        this.load(true);
      },
      error: (error) => this.fail(error),
    });
  }

  deleteSelected(): void {
    if (!this.selected) return;
    const order = this.selected;
    this.confirmations.confirm({
      header: "Delete article request",
      message: `Delete “${order.campaign_name}” and its payment and activity records? This cannot be undone.`,
      icon: "pi pi-exclamation-triangle",
      acceptButtonStyleClass: "p-button-danger",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      accept: () => {
        this.saving = true;
        this.ordersService.delete(order.id).pipe(
          finalize(() => {
            this.saving = false;
            this.changeDetector.markForCheck();
          }),
        ).subscribe({
          next: () => {
            this.selected = null;
            this.messages.add({ severity: "success", summary: "Request deleted", detail: "The article request was removed." });
            this.load();
          },
          error: (error) => this.fail(error),
        });
      },
    });
  }

  severity(status: string): "success" | "info" | "warn" | "danger" | "secondary" {
    if (status === "published" || status === "paid") return "success";
    if (["quote_requested", "changes_requested"].includes(status)) return "warn";
    if (["payment_pending", "under_review", "accepted", "quote_sent"].includes(status)) return "info";
    return "secondary";
  }

  label(value: string): string { return (value || "unknown").replaceAll("_", " "); }
  validUrl(value: string): boolean { try { return ["http:", "https:"].includes(new URL(value).protocol); } catch { return false; } }
  validEmail(value: string): boolean { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()); }

  private fail(error: any): void {
    this.saving = false;
    this.messages.add({ severity: "error", summary: "Update failed", detail: error?.error?.message || "Please try again." });
  }
}
